// /src/components/ProfileManagement.js

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { 
  FaUserCircle, 
  FaEnvelope, 
  FaPhone, 
  FaLock,
  FaSpinner,
  FaTimes,
  FaExclamationTriangle,
  FaCheck,
  FaEdit,
  FaShieldAlt,
  FaIdCard,
  FaGlobe,
  FaSave,
  FaCalendar,
  FaHistory,
  FaSignOutAlt
} from 'react-icons/fa';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { useTranslation } from 'react-i18next';

const LanguageSection = ({ isEditing, onEdit, onSave, onCancel, isSaving, setSuccess, setError }) => {
  const { t, i18n } = useTranslation();
  const [selectedLanguage, setSelectedLanguage] = useState(i18n.language);

  const languages = [
    { code: 'en', name: t('profile.sections.language.fields.language.options.en') },
    { code: 'es', name: t('profile.sections.language.fields.language.options.es') },
    { code: 'fr', name: t('profile.sections.language.fields.language.options.fr') },
    { code: 'de', name: t('profile.sections.language.fields.language.options.de') }
  ];

  const handleLanguageChange = async (langCode) => {
    try {
      setSelectedLanguage(langCode);
      await i18n.changeLanguage(langCode);
      localStorage.setItem('preferredLanguage', langCode);
      setSuccess('LANGUAGE_UPDATE_SUCCESS');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      setError('LANGUAGE_UPDATE_ERROR');
    }
  };

  return (
    <ProfileSection
      title={t('profile.sections.language.title')}
      icon={FaGlobe}
      isEditing={isEditing}
      onEdit={onEdit}
      onSave={() => {
        handleLanguageChange(selectedLanguage);
        onSave();
      }}
      onCancel={onCancel}
      isSaving={isSaving}
    >
      {isEditing ? (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              {t('profile.sections.language.fields.language.label')}
            </label>
            <select
              value={selectedLanguage}
              onChange={(e) => setSelectedLanguage(e.target.value)}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg 
                       text-white focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
            >
              {languages.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.name}
                </option>
              ))}
            </select>
          </div>
          <p className="text-sm text-gray-400">
            {t('profile.sections.language.messages.changeWarning')}
          </p>
        </div>
      ) : (
        <div className="flex items-center">
          <FaGlobe className="text-gray-400 mr-2" />
          <span className="text-gray-300">{t('profile.sections.language.fields.language.label')}:</span>
          <span className="text-white ml-2">
            {languages.find(lang => lang.code === i18n.language)?.name}
          </span>
        </div>
      )}
    </ProfileSection>
  );
};

const ProfileSection = ({ 
  title, 
  icon: Icon, 
  children, 
  onEdit, 
  isEditing, 
  onSave, 
  onCancel, 
  isSaving 
}) => (
  <Card className="bg-gray-800 border-gray-700">
    <CardHeader className="flex flex-row items-center justify-between">
      <div className="flex items-center space-x-2">
        <Icon className="text-indigo-400 h-5 w-5" />
        <CardTitle className="text-white">{title}</CardTitle>
      </div>
      {!isEditing ? (
        <button
          onClick={onEdit}
          className="p-2 text-gray-400 hover:text-gray-300 hover:bg-gray-700 rounded-lg 
                   transition-colors duration-200"
        >
          <FaEdit />
        </button>
      ) : (
        <div className="flex space-x-2">
          <button
            onClick={onCancel}
            disabled={isSaving}
            className="p-2 text-red-400 hover:text-red-300 hover:bg-gray-700 rounded-lg 
                     transition-colors duration-200"
          >
            <FaTimes />
          </button>
          <button
            onClick={onSave}
            disabled={isSaving}
            className="p-2 text-green-400 hover:text-green-300 hover:bg-gray-700 rounded-lg 
                     transition-colors duration-200"
          >
            {isSaving ? <FaSpinner className="animate-spin" /> : <FaSave />}
          </button>
        </div>
      )}
    </CardHeader>
    <CardContent>
      {children}
    </CardContent>
  </Card>
);

const ProfileManagement = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  // Profile States
  const [profile, setProfile] = useState({
    username: '',
    email: '',
    phone: '',
    fullName: '',
    location: '',
    role: '',
    joinDate: '',
    bio: ''
  });

  // Edit States
  const [editingSection, setEditingSection] = useState(null);
  const [editedValues, setEditedValues] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Password Change States
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await api.get('/users/profile', {
        headers: { 'x-auth-token': localStorage.getItem('token') }
      });
      setProfile(response.data);
    } catch (error) {
      setError('Failed to load profile');
      console.error('Failed to fetch profile:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (section) => {
    setEditingSection(section);
    setEditedValues({
      ...profile
    });
    setError(null);
    setSuccess(null);
  };

  const handleCancel = () => {
    setEditingSection(null);
    setEditedValues({});
    setError(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedValues(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSaveProfile = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const response = await api.put(
        '/users/profile',
        editedValues,
        { headers: { 'x-auth-token': localStorage.getItem('token') } }
      );
      setProfile(response.data);
      setEditingSection(null);
      setSuccess('PROFILE_UPDATE_SUCCESS');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      setError(error.response?.data?.error || 'SERVER_ERROR');
    } finally {
      setIsSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('PASSWORDS_MISMATCH');
      return;
    }
  
    setIsSaving(true);
    setError(null);
    try {
      await api.put(
        '/users/change-password',
        {
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword
        },
        { headers: { 'x-auth-token': localStorage.getItem('token') } }
      );
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setEditingSection(null);
      setSuccess('PASSWORD_CHANGE_SUCCESS');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      setError(error.response?.data?.error || 'SERVER_ERROR');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <FaSpinner className="animate-spin text-indigo-500 text-4xl" />
      </div>
    );
  }

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('guestToken');
    localStorage.removeItem('role');
    navigate('/login');
  };

  return (
    <div className="flex-1 space-y-8 p-8 pt-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-white">{t('profile.title')}</h2>
          <p className="mt-2 text-gray-400">{t('profile.subtitle')}</p>
        </div>
        <button
          onClick={handleLogout}
          className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg 
                   transition-colors duration-200 flex items-center"
        >
          <FaSignOutAlt className="mr-2" />
          {t('sidebar.userMenu.logout')}
        </button>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4 flex items-center">
          <FaExclamationTriangle className="text-red-500 mr-2" />
          <span className="text-red-500">
            {t(`profile.errors.${error}`, {
              defaultValue: t('profile.errors.SERVER_ERROR')
            })}
          </span>
          <button
            onClick={() => setError(null)}
            className="ml-auto text-red-500 hover:text-red-400"
          >
            <FaTimes />
          </button>
        </div>
      )}

      {success && (
        <div className="bg-green-500/10 border border-green-500/50 rounded-lg p-4 flex items-center">
          <FaCheck className="text-green-500 mr-2" />
          <span className="text-green-500">
            {t(`profile.success.${success}`, {
              defaultValue: t('profile.success.GENERIC_SUCCESS')
            })}
          </span>
        </div>
      )}

      {/* Basic Information */}
      <ProfileSection
        title={t('profile.sections.basicInfo.title')}
        icon={FaIdCard}
        isEditing={editingSection === 'basic'}
        onEdit={() => handleEdit('basic')}
        onSave={handleSaveProfile}
        onCancel={handleCancel}
        isSaving={isSaving}
      >
        {editingSection === 'basic' ? (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                {t('profile.sections.basicInfo.fields.fullName.label')}
              </label>
              <input
                type="text"
                name="fullName"
                placeholder={t('profile.sections.basicInfo.fields.fullName.placeholder')}
                value={editedValues.fullName || ''}
                onChange={handleInputChange}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg 
                         text-white focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                {t('profile.sections.basicInfo.fields.email.label')}
              </label>
              <input
                type="email"
                name="email"
                placeholder={t('profile.sections.basicInfo.fields.email.placeholder')}
                value={editedValues.email || ''}
                onChange={handleInputChange}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg 
                         text-white focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                {t('profile.sections.basicInfo.fields.phone.label')}
              </label>
              <input
                type="tel"
                name="phone"
                placeholder={t('profile.sections.basicInfo.fields.phone.placeholder')}
                value={editedValues.phone || ''}
                onChange={handleInputChange}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg 
                         text-white focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
              />
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center">
              <FaUserCircle className="text-gray-400 mr-2" />
              <span className="text-gray-300">Full Name:</span>
              <span className="text-white ml-2">{profile.fullName || 'Not set'}</span>
            </div>
            <div className="flex items-center">
              <FaEnvelope className="text-gray-400 mr-2" />
              <span className="text-gray-300">Email:</span>
              <span className="text-white ml-2">{profile.email || 'Not set'}</span>
            </div>
            <div className="flex items-center">
              <FaPhone className="text-gray-400 mr-2" />
              <span className="text-gray-300">Phone:</span>
              <span className="text-white ml-2">{profile.phone || 'Not set'}</span>
            </div>
          </div>
        )}
      </ProfileSection>

      {/* Additional Information */}
      <ProfileSection
        title={t('profile.sections.additionalInfo.title')}
        icon={FaGlobe}
        isEditing={editingSection === 'additional'}
        onEdit={() => handleEdit('additional')}
        onSave={handleSaveProfile}
        onCancel={handleCancel}
        isSaving={isSaving}
      >
        {editingSection === 'additional' ? (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                {t('profile.sections.additionalInfo.fields.location.label')}
              </label>
              <input
                type="text"
                name="location"
                placeholder={t('profile.sections.additionalInfo.fields.location.placeholder')}
                value={editedValues.location || ''}
                onChange={handleInputChange}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg 
                         text-white focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                {t('profile.sections.additionalInfo.fields.bio.label')}
              </label>
              <textarea
                name="bio"
                placeholder={t('profile.sections.additionalInfo.fields.bio.placeholder')}
                value={editedValues.bio || ''}
                onChange={handleInputChange}
                rows="3"
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg 
                         text-white focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
              />
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center">
              <FaGlobe className="text-gray-400 mr-2" />
              <span className="text-gray-300">{t('profile.sections.additionalInfo.fields.location.label')}:</span>
              <span className="text-white ml-2">{profile.location || t('common.status.inactive')}</span>
            </div>
            <div>
              <span className="text-gray-300">{t('profile.sections.additionalInfo.fields.bio.label')}:</span>
              <p className="text-white mt-1">{profile.bio || t('common.status.inactive')}</p>
            </div>
          </div>
        )}
      </ProfileSection>

      {/* Account Security */}
      <ProfileSection
        title={t('profile.sections.security.title')}
        icon={FaShieldAlt}
        isEditing={editingSection === 'security'}
        onEdit={() => handleEdit('security')}
        onSave={handleChangePassword}
        onCancel={handleCancel}
        isSaving={isSaving}
      >
        {editingSection === 'security' ? (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                {t('profile.sections.security.fields.currentPassword.label')}
              </label>
              <input
                type="password"
                name="currentPassword"
                placeholder={t('profile.sections.security.fields.currentPassword.placeholder')}
                value={passwordData.currentPassword}
                onChange={handlePasswordChange}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg 
                         text-white focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                {t('profile.sections.security.fields.newPassword.label')}
              </label>
              <input
                type="password"
                name="newPassword"
                placeholder={t('profile.sections.security.fields.newPassword.placeholder')}
                value={passwordData.newPassword}
                onChange={handlePasswordChange}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg 
                         text-white focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                {t('profile.sections.security.fields.confirmPassword.label')}
              </label>
              <input
                type="password"
                name="confirmPassword"
                placeholder={t('profile.sections.security.fields.confirmPassword.placeholder')}
                value={passwordData.confirmPassword}
                onChange={handlePasswordChange}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg 
                         text-white focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
              />
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center">
              <FaLock className="text-gray-400 mr-2" />
              <span className="text-gray-300">{t('profile.sections.security.fields.currentPassword.label')}:</span>
              <span className="text-white ml-2">{t('profile.sections.security.messages.passwordMask')}</span>
            </div>
            <p className="text-sm text-gray-400">
              {t('profile.sections.security.messages.passwordHint')}
            </p>
          </div>
        )}
      </ProfileSection>

      {/* Account Information (Read-only) */}
      <ProfileSection
        title={t('profile.sections.accountInfo.title')}
        icon={FaUserCircle}
        isEditing={false}
      >
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <FaIdCard className="text-gray-400 mr-2" />
              <span className="text-gray-300">{t('profile.sections.accountInfo.fields.username.label')}:</span>
              <span className="text-white ml-2">{profile.username}</span>
            </div>
            <span className="text-gray-500 text-sm">
              {t('profile.sections.accountInfo.fields.username.cannotChange')}
            </span>
          </div>
          <div className="flex items-center">
            <FaShieldAlt className="text-gray-400 mr-2" />
            <span className="text-gray-300">{t('profile.sections.accountInfo.fields.role.label')}:</span>
            <span className="ml-2">
              <span className={`px-2 py-1 rounded-full text-xs font-medium
                ${profile.role === 'admin' ? 'bg-red-500/20 text-red-400' :
                  profile.role === 'user' ? 'bg-blue-500/20 text-blue-400' :
                  'bg-green-500/20 text-green-400'}`}>
                {t(`profile.sections.accountInfo.fields.role.types.${profile.role}`)}
              </span>
            </span>
          </div>
          <div className="flex items-center">
            <FaCalendar className="text-gray-400 mr-2" />
            <span className="text-gray-300">{t('profile.sections.accountInfo.fields.memberSince.label')}:</span>
            <span className="text-white ml-2">
              {new Date(profile.joinDate).toLocaleDateString()}
            </span>
          </div>
          <div className="mt-4 pt-4 border-t border-gray-700">
            <p className="text-sm text-gray-400">
              Some account information cannot be changed for security reasons. 
              Please contact support if you need to update these details.
            </p>
          </div>
        </div>
      </ProfileSection>

      {/* Language Settings */}
      <LanguageSection
        isEditing={editingSection === 'language'}
        onEdit={() => handleEdit('language')}
        onSave={handleSaveProfile}
        onCancel={handleCancel}
        isSaving={isSaving}
        setSuccess={setSuccess}
        setError={setError}
      />

      {/* Account Activity (Optional) */}
      <ProfileSection
        title={t('profile.sections.activity.title')}
        icon={FaHistory}
        isEditing={false}
      >
        <div className="space-y-4">
          <div className="text-sm text-gray-400">
            {t('profile.sections.activity.fields.lastLogin')}: {
              profile.lastLogin ? 
              new Date(profile.lastLogin).toLocaleString() : 
              t('profile.sections.activity.fields.never')
            }
          </div>
          <div className="text-sm text-gray-400">
            {t('profile.sections.activity.fields.lastPasswordChange')}: {
              profile.lastPasswordChange ? 
              new Date(profile.lastPasswordChange).toLocaleString() : 
              t('profile.sections.activity.fields.never')
            }
          </div>
        </div>
      </ProfileSection>
    </div>
  );
};

export default ProfileManagement;